import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Check, X, Sparkles, Zap, Crown, GraduationCap,
  ArrowRight, Percent, IndianRupee, Shield,
  BookOpen, Award
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature Item Component (moved outside main component)
const FeatureItem = ({ feature }) => (
  <div className={`flex items-start gap-3 py-2`}>
    {feature.included ? (
      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${feature.highlight ? 'bg-emerald-500 text-white' : 'bg-emerald-500 text-white'}`}>
        <Check className="w-3 h-3 stroke-[3]" />
      </div>
    ) : (
      <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
        <X className="w-3 h-3 text-red-500 stroke-[3]" />
      </div>
    )}
    <span className={`text-sm ${feature.included ? (feature.highlight ? 'font-semibold text-foreground' : 'text-foreground') : 'text-muted-foreground line-through'}`}>
      {feature.name}
    </span>
  </div>
);

// Pricing Card Component (moved outside main component)
const PricingCard = ({ 
  tier, 
  isPremium, 
  appliedCoupon, 
  couponCode, 
  setCouponCode, 
  validatingCoupon, 
  onValidateCoupon, 
  onEnroll 
}) => {
  if (!tier || !tier.enabled) return null;

  const getPrice = () => {
    if (appliedCoupon && appliedCoupon.tierType === tier.tier_type) {
      return appliedCoupon.final_price;
    }
    return tier.price;
  };

  const finalPrice = getPrice();
  const hasDiscount = appliedCoupon?.tierType === tier.tier_type || tier.original_price;
  const originalPrice = appliedCoupon?.tierType === tier.tier_type 
    ? tier.price 
    : tier.original_price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: isPremium ? 0.1 : 0 }}
      className="relative"
    >
      {/* Premium Badge */}
      {isPremium && tier.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 text-sm font-semibold shadow-lg">
            <Crown className="w-3.5 h-3.5 mr-1.5" />
            {tier.badge}
          </Badge>
        </div>
      )}

      <Card className={`h-full ${isPremium 
        ? 'border-2 border-emerald-500/50 bg-gradient-to-b from-emerald-500/5 to-transparent shadow-xl shadow-emerald-500/10' 
        : 'border border-border'}`}
      >
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            {isPremium ? (
              <GraduationCap className="w-6 h-6 text-emerald-500" />
            ) : (
              <BookOpen className="w-6 h-6 text-blue-500" />
            )}
            <CardTitle className="text-xl">{tier.name}</CardTitle>
          </div>
          <CardDescription className="text-sm min-h-[40px]">
            {tier.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Price */}
          <div className="text-center py-4 border-y border-border/50">
            {hasDiscount && originalPrice && (
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-lg text-muted-foreground line-through">
                  ₹{originalPrice.toLocaleString('en-IN')}
                </span>
                {appliedCoupon?.tierType === tier.tier_type && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                    <Percent className="w-3 h-3 mr-1" />
                    {appliedCoupon.discount_type === 'percentage' 
                      ? `${appliedCoupon.discount_value}% OFF`
                      : `₹${appliedCoupon.discount_amount.toLocaleString('en-IN')} OFF`
                    }
                  </Badge>
                )}
              </div>
            )}
            <div className="flex items-baseline justify-center gap-1">
              <IndianRupee className="w-6 h-6" />
              <span className="text-4xl font-bold">{finalPrice.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">One-time payment</p>
          </div>

          {/* Coupon Input */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => onValidateCoupon(tier.tier_type, tier.price)}
                disabled={validatingCoupon}
              >
                {validatingCoupon ? '...' : 'Apply'}
              </Button>
            </div>
            {appliedCoupon?.tierType === tier.tier_type && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Coupon &ldquo;{appliedCoupon.code}&rdquo; applied! Save ₹{appliedCoupon.discount_amount.toLocaleString('en-IN')}
              </p>
            )}
          </div>

          {/* Features */}
          <div className="space-y-1">
            <p className="text-sm font-semibold mb-3">What&apos;s included:</p>
            {tier.features?.map((feature, idx) => (
              <FeatureItem key={idx} feature={feature} />
            ))}
          </div>

          {/* CTA Button */}
          <Button
            className={`w-full h-12 text-base font-semibold ${isPremium 
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/25' 
              : ''}`}
            variant={isPremium ? 'default' : 'outline'}
            onClick={() => onEnroll(tier.tier_type)}
          >
            {tier.cta_text}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Trust Badges */}
          {isPremium && (
            <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" />
                Secure Payment
              </span>
              <span className="flex items-center gap-1">
                <Award className="w-3.5 h-3.5" />
                Certificate
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function CoursePricing({ courseId, courseTitle, pricingTiers, onEnroll }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const selfLearning = pricingTiers?.self_learning;
  const instructorLed = pricingTiers?.instructor_led;

  const validateCoupon = async (tierType, price) => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        course_id: courseId,
        tier_type: tierType,
        price: price
      });
      setAppliedCoupon({ ...response.data, tierType });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleEnroll = (tierType) => {
    // Navigate directly to checkout page (no login required)
    const coupon = appliedCoupon?.tierType === tierType ? appliedCoupon.code : null;
    navigate(`/checkout/${courseId}?tier=${tierType}${coupon ? `&coupon=${coupon}` : ''}`);
  };

  if (!pricingTiers) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Pricing information not available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          Choose Your Learning Path
        </Badge>
        <h2 className="text-2xl md:text-3xl font-bold">
          Invest in Your Creative Future
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Whether you&apos;re a self-starter or prefer guided learning, we have a path for you.
          Same curriculum, different experiences.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <PricingCard 
          tier={selfLearning} 
          isPremium={false}
          appliedCoupon={appliedCoupon}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          validatingCoupon={validatingCoupon}
          onValidateCoupon={validateCoupon}
          onEnroll={handleEnroll}
        />
        <PricingCard 
          tier={instructorLed} 
          isPremium={true}
          appliedCoupon={appliedCoupon}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          validatingCoupon={validatingCoupon}
          onValidateCoupon={validateCoupon}
          onEnroll={handleEnroll}
        />
      </div>

      {/* Comparison Highlight */}
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 rounded-2xl p-6 max-w-4xl mx-auto">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg mb-1">Why Instructor-Led?</h3>
            <p className="text-sm text-muted-foreground">
              The Instructor-Led program isn&apos;t just about learning—it&apos;s about <strong>transformation</strong>. 
              With personal mentorship, live feedback, and industry connections, you&apos;re not just gaining skills—you&apos;re building a career. 
              Our placement rate for Instructor-Led students is <strong>98%</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ or Additional Info */}
      <div className="text-center text-sm text-muted-foreground">
        <p>Have questions? <a href="/contact" className="text-emerald-500 hover:underline">Contact our admissions team</a></p>
      </div>
    </div>
  );
}
